import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import { DEALERSHIP_INFO } from '../config/dealership';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-[#020405] pt-20" data-testid="privacy-policy-page">
      {/* Header */}
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#16202A] via-[#0A0F14] to-[#020405]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-4"
          >
            <div className="w-12 h-12 flex items-center justify-center bg-[#48CCCD]/20">
              <Shield size={24} className="text-[#48CCCD]" />
            </div>
            <div>
              <h1 
                className="font-serif text-3xl md:text-4xl font-bold text-[#F8FAFC] tracking-tight"
                data-testid="privacy-title"
              >
                Privacy Policy
              </h1>
              <p className="text-[#94A3B8] font-sans text-sm">Last updated: January 2025</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-16">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="prose prose-invert max-w-none"
          >
            <div className="space-y-8 text-[#94A3B8] font-sans">
              {/* Introduction */}
              <div className="bg-[#0A0F14] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[#F8FAFC] mb-4">About This Policy</h2>
                <p className="leading-relaxed">
                  {DEALERSHIP_INFO.name} respects your privacy. This policy explains what information we collect 
                  when you use our website, why we collect it, and how we handle it.
                </p>
              </div>

              {/* What We Collect */}
              <div className="bg-[#0A0F14] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[#F8FAFC] mb-4">What Information We Collect</h2>
                <p className="mb-4 leading-relaxed">When you submit an enquiry or contact form, we may collect:</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Your name</li>
                  <li>Email address</li>
                  <li>Phone number</li>
                  <li>Vehicle details (make, model, year, registration)</li>
                  <li>Photos of your vehicle (if you choose to upload them)</li>
                  <li>Any message or notes you provide</li>
                </ul>
              </div>

              {/* Why We Collect */}
              <div className="bg-[#0A0F14] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[#F8FAFC] mb-4">Why We Collect This Information</h2>
                <p className="mb-4 leading-relaxed">We use your information to:</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Respond to your enquiries about vehicles</li>
                  <li>Process requests to sell your car</li>
                  <li>Arrange repair or service appointments</li>
                  <li>Contact you regarding your enquiry</li>
                  <li>Provide finance estimates (where applicable)</li>
                </ul>
              </div>

              {/* How We Handle Data */}
              <div className="bg-[#0A0F14] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[#F8FAFC] mb-4">How We Handle Your Data</h2>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Your data is stored securely and used only for the purposes described above</li>
                  <li>Enquiry details are emailed to our team so we can respond to you</li>
                  <li><span className="text-[#F8FAFC] font-medium">We do not sell your personal information to third parties</span></li>
                  <li>We do not share your data with external marketing companies</li>
                  <li>Uploaded photos are stored securely and used only to assess your vehicle</li>
                </ul>
              </div>

              {/* Your Rights */}
              <div className="bg-[#0A0F14] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[#F8FAFC] mb-4">Your Rights</h2>
                <p className="leading-relaxed">
                  You can contact us at any time to request access to the information we hold about you, 
                  ask us to correct any errors, or request that we delete your data.
                </p>
              </div>

              {/* Contact */}
              <div className="bg-[#0A0F14] border border-white/10 p-6 md:p-8">
                <h2 className="text-xl font-serif font-bold text-[#F8FAFC] mb-4">Contact Us</h2>
                <p className="leading-relaxed mb-4">
                  If you have any questions about this privacy policy or how we handle your data, please contact us:
                </p>
                <div className="space-y-2">
                  <p><span className="text-[#F8FAFC]">Business:</span> {DEALERSHIP_INFO.name}</p>
                  <p><span className="text-[#F8FAFC]">Email:</span> <a href={DEALERSHIP_INFO.emailHref} className="text-[#48CCCD] hover:underline">{DEALERSHIP_INFO.email}</a></p>
                  <p><span className="text-[#F8FAFC]">Phone:</span> <a href={DEALERSHIP_INFO.phoneHref} className="text-[#48CCCD] hover:underline">{DEALERSHIP_INFO.phone}</a></p>
                  <p><span className="text-[#F8FAFC]">Address:</span> {DEALERSHIP_INFO.address.full}</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicyPage;
